import { OutgoingRequestDelegate } from 'sip.js/lib/core';
import { SessionManager } from 'sip.js/lib/platform/web';
import { SoftphoneTypes } from '@frontend/api-softphone';

export type SoftphoneClientCredentials = {
  proxy: string;
  username: string;
  domain: SoftphoneTypes.FullyQualifiedAddress;
  password: string;
};

type ConnectSessionOptions = {
  session?: SessionManager;
  onConnecting: () => void;
  onConnectionFailed: (error: Error) => void;
  requestDelegate: OutgoingRequestDelegate;
};

type DisconnectSessionOptions = {
  session?: SessionManager;
  onBeforeDisconnect?: () => void;
};

type CreateSessionOptions = ConnectSessionOptions & {
  credentials: SoftphoneClientCredentials;
  createSessionManager: (credentials: SoftphoneClientCredentials) => SessionManager;
  onClientReady: (session: SessionManager) => void;
};

export class SoftphoneClientController {
  private client: SessionManager | undefined;
  private connectPromise: Promise<void> | null = null;
  private disconnectPromise: Promise<void> | null = null;
  private isCreatingSession = false;

  getClient() {
    return this.client;
  }

  setClient(session: SessionManager | undefined) {
    this.client = session;
  }

  connect({ session = this.client, onConnecting, onConnectionFailed, requestDelegate }: ConnectSessionOptions) {
    if (!session) {
      return;
    }

    if (this.connectPromise) {
      return this.connectPromise;
    }

    const connectPromise = (async () => {
      if (this.disconnectPromise) {
        await this.disconnectPromise.catch(() => undefined);
      }

      onConnecting();

      try {
        await session.connect();
        await session.register({
          requestDelegate,
        });
      } catch (err) {
        console.log('Error connecting softphone', err);
        onConnectionFailed(new Error('Error connecting softphone'));
        throw err;
      }
    })();

    this.connectPromise = connectPromise.finally(() => {
      this.connectPromise = null;
    });

    return this.connectPromise;
  }

  disconnect({ session = this.client, onBeforeDisconnect }: DisconnectSessionOptions) {
    if (!session) {
      return;
    }

    if (this.disconnectPromise) {
      return this.disconnectPromise;
    }

    const disconnectPromise = (async () => {
      if (this.connectPromise) {
        await this.connectPromise.catch(() => undefined);
      }

      try {
        onBeforeDisconnect?.();
        // session.disconnect() sends a SIP UNREGISTER and waits for a 200 OK from the server.
        // In cross-tab scenarios, the server may have already invalidated this tab's registration
        // (because another tab just registered with the same credentials), so the 200 OK never
        // arrives and session.disconnect() hangs indefinitely. The timeout ensures the finally
        // block (which force-closes the WebSocket via transport.disconnect()) always runs.
        await Promise.race([
          session.disconnect(),
          new Promise<void>((_, reject) => setTimeout(() => reject(new Error('disconnect timeout')), 3000)),
        ]);
      } catch (err) {
        console.log('Error disconnecting softphone', err);
      } finally {
        try {
          await session.userAgent.transport.disconnect();
        } catch {
          // Transport may already be closed.
        }
      }
    })();

    this.disconnectPromise = disconnectPromise.finally(() => {
      this.disconnectPromise = null;
    });

    return this.disconnectPromise;
  }

  createSession({
    credentials,
    createSessionManager,
    onConnecting,
    onConnectionFailed,
    onClientReady,
    requestDelegate,
  }: CreateSessionOptions) {
    if (this.isCreatingSession) {
      return;
    }

    this.isCreatingSession = true;
    const session = createSessionManager(credentials);

    const connectPromise = this.connect({
      session,
      onConnecting,
      onConnectionFailed,
      requestDelegate,
    });

    if (!connectPromise) {
      this.isCreatingSession = false;
      return session;
    }

    void connectPromise
      .then(() => {
        this.client = session;
        onClientReady(session);
      })
      .catch((err) => {
        console.log('Error creating softphone session', err);
        void this.disconnect({ session });
      })
      .finally(() => {
        this.isCreatingSession = false;
      });

    return session;
  }
}
